import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiX, FiHome } from 'react-icons/fi';
import { nacmeLogo, tlaLogo } from './assets/images';
import { MobileAlumniNav, MobileAlumniBottomNav, MobileJobCard } from './components/AlumniMobileComponents';
import { ResponsiveTable } from './components/ResponsiveComponents';
import { IS_TLA, PATHS } from './utils/platformPaths';

const AlumniJobBoard = () => {
  const navigate = useNavigate();
  const searchTimeout = useRef(null);
  const [jobPostings, setJobPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    employment_type: '',
    industry: '',
    company_id: ''
  });
  
  const [activeFilters, setActiveFilters] = useState({});
  
  // Mobile state
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Recommended tab state
  const [activeTab, setActiveTab] = useState('browse');
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [recommendedError, setRecommendedError] = useState('');
  const [recommendedPage, setRecommendedPage] = useState(1);
  const [recommendedTotalPages, setRecommendedTotalPages] = useState(1);
  const [hasActiveResume, setHasActiveResume] = useState(true);

  const employmentTypes = [
    'full-time',
    'part-time', 
    'contract',
    'internship',
    'temporary'
  ];

  const industries = [
    'Technology',
    'Engineering',
    'Finance',
    'Healthcare',
    'Manufacturing',
    'Energy',
    'Aerospace',
    'Automotive',
    'Consulting'
  ];

  useEffect(() => {
    fetchJobPostings();
  }, [currentPage, activeFilters]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Fetch recommended jobs when tab is active
  useEffect(() => {
    if (activeTab === 'recommended') {
      fetchRecommendedJobs();
    }
  }, [activeTab, recommendedPage]);

  const fetchRecommendedJobs = async () => {
    setRecommendedLoading(true);
    setRecommendedError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const params = new URLSearchParams({
        page: recommendedPage.toString(),
        limit: '20',
        min_similarity: '0.3'
      });

      const response = await fetch(`${backendUrl}/api/alumni/job-postings/recommended?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        throw new Error(`Failed to fetch recommendations: ${response.status}`);
      }

      const data = await response.json();
      setHasActiveResume(data.has_active_resume !== false);
      setRecommendedJobs(data.jobs || []);
      setRecommendedTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Error fetching recommended jobs:', err);
      setRecommendedError('Failed to load recommendations');
    } finally {
      setRecommendedLoading(false);
    }
  };

  const getMatchBadgeColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (percentage >= 60) return 'bg-blue-100 text-blue-800 border-blue-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const fetchJobPostings = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const searchParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...activeFilters
      });
      
      // Remove empty filters and ensure proper types
      for (let [key, value] of searchParams.entries()) {
        if (!value || value === '' || value === 'null' || value === 'undefined' || value === 'all') {
          searchParams.delete(key);
        }
      }

      const response = await fetch(`${backendUrl}/api/alumni/job-postings?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        const errorData = await response.text();
        console.error('API Error:', response.status, errorData);
        throw new Error(`Failed to fetch job postings: ${response.status} - ${errorData}`);
      }

      const data = await response.json();
      setJobPostings(data.jobs || data.job_postings || []);
      setTotalPages(data.total_pages || Math.ceil((data.total || 0) / 20));
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching job postings:', err);
      setError('Failed to load job postings');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value
    });
    
    // Auto-apply search filter with debouncing
    if (key === 'search') {
      // Clear existing timeout
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      
      // Set new timeout for search
      searchTimeout.current = setTimeout(() => {
        const updatedFilters = { ...activeFilters };
        if (value && value !== '') {
          updatedFilters.search = value;
        } else {
          delete updatedFilters.search;
        }
        setActiveFilters(updatedFilters);
        setCurrentPage(1);
      }, 500); // 500ms delay
    }
  };

  const applyFilters = () => {
    // Only include non-empty filters
    const cleanFilters = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '' && value !== 'all') {
        cleanFilters[key] = value;
      }
    });
    setActiveFilters(cleanFilters);
    setCurrentPage(1); // Reset to first page
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      employment_type: '',
      industry: '',
      company_id: ''
    });
    setActiveFilters({});
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return null;
    if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    if (min) return `$${min.toLocaleString()}+`;
    if (max) return `Up to $${max.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const trackJobClick = async (jobId, clickType) => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      await fetch(`${backendUrl}/api/alumni/job-click`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_id: jobId,
          click_type: clickType,
          user_agent: navigator.userAgent,
          referrer: window.location.href
        })
      });
    } catch (error) {
      console.error('Failed to track job click:', error);
      // Don't block the user action if tracking fails
    }
  };

  const handleJobView = async (job) => {
    await trackJobClick(job.id, 'view');
  };

  const handleApplyToJob = async (job) => {
    // Track the apply click
    await trackJobClick(job.id, 'apply');
    
    if (job.application_method === 'external' && job.application_url) {
      // Track external link click
      await trackJobClick(job.id, 'external_link');
      window.open(job.application_url, '_blank');
    } else if (job.contact_email) {
      window.location.href = `mailto:${job.contact_email}?subject=Application for ${job.title}&body=Dear Hiring Manager,%0A%0AI am interested in applying for the ${job.title} position at ${job.company.name}.%0A%0ABest regards`;
    }
  };

  if (loading && jobPostings.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading job opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Navigation */}
      <div className="md:hidden">
        <MobileAlumniNav 
          title="Job Board"
          showBack={true}
          onBackClick={() => navigate(PATHS.dashboard)}
        />
      </div>

      {/* Desktop Header */}
      <header className="hidden md:block bg-white shadow sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img className={`w-auto ${IS_TLA ? 'h-32' : 'h-10'}`} src={IS_TLA ? tlaLogo : nacmeLogo} alt={IS_TLA ? "TLA Logo" : "NACME Logo"} />
              <h1 className="ml-4 text-xl font-bold text-gray-900">{IS_TLA ? 'TLA Job Board' : 'Alumni Job Board'}</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">{activeTab === 'browse' ? total : recommendedJobs.length} opportunities</span>
              <button
                onClick={() => navigate(PATHS.dashboard)}
                className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
              >
                <FiHome className="h-4 w-4 mr-2" />
                Dashboard
              </button>
            </div>
          </div>
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 -mb-px">
            <button
              onClick={() => setActiveTab('browse')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'browse'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Browse All Jobs
            </button>
            <button
              onClick={() => setActiveTab('recommended')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'recommended'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Recommended For You
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Layout */}
      <div className="md:hidden pb-20"> {/* pb-20 for bottom nav */}
        {/* Mobile Tab Navigation */}
        <div className="bg-white border-b border-gray-200 flex">
          <button
            onClick={() => setActiveTab('browse')}
            className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
              activeTab === 'browse'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            Browse All
          </button>
          <button
            onClick={() => setActiveTab('recommended')}
            className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
              activeTab === 'recommended'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            Recommended
          </button>
        </div>

        {/* Mobile Browse Tab Content */}
        {activeTab === 'browse' && (<>
        <div className="bg-white shadow-sm p-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search jobs..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <button
              onClick={() => setShowMobileFilters(true)}
              className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors flex items-center"
            >
              <FiFilter className="w-4 h-4" />
            </button>
          </div>

          {/* Active Filters Display */}
          {Object.keys(activeFilters).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs text-gray-500 mr-2">{total} jobs found</span>
              {Object.entries(activeFilters).map(([key, value]) => (
                <span
                  key={key}
                  className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full"
                >
                  {key}: {value}
                  <button
                    onClick={() => {
                      const newFilters = { ...activeFilters };
                      delete newFilters[key];
                      setActiveFilters(newFilters);
                      setFilters({ ...filters, [key]: '' });
                    }}
                    className="ml-1 text-orange-600 hover:text-orange-800"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Mobile Filters Modal */}
        {showMobileFilters && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
            <div className="bg-white h-full max-w-sm ml-auto">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4 overflow-y-auto h-full pb-20">
                {/* Mobile Location Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                  <input
                    type="text"
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    placeholder="City, state, or remote"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                {/* Mobile Employment Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={filters.employment_type}
                    onChange={(e) => handleFilterChange('employment_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">All Types</option>
                    {employmentTypes.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mobile Industry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                  <select
                    value={filters.industry}
                    onChange={(e) => handleFilterChange('industry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">All Industries</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                {/* Mobile Filter Actions */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3">
                  <button
                    onClick={() => {
                      applyFilters();
                      setShowMobileFilters(false);
                    }}
                    className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                  >
                    Apply Filters
                  </button>
                  <button
                    onClick={() => {
                      clearFilters();
                      setShowMobileFilters(false);
                    }}
                    className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Mobile Job Cards */}
        <div className="px-4 mt-4">
          {jobPostings.length === 0 && !loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No job opportunities found.</p>
              <button
                onClick={clearFilters}
                className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {jobPostings.map((job) => (
                <MobileJobCard
                  key={job.id}
                  job={job}
                  onApply={() => handleApplyToJob(job)}
                  onViewDetails={() => handleJobView(job)}
                  onSave={() => {/* Implement job saving */}}
                />
              ))}

              {/* Mobile Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-4 py-6">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        </>)}

        {/* Mobile Recommended Tab Content */}
        {activeTab === 'recommended' && (
          <div className="px-4 mt-4">
            {recommendedLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto"></div>
                <p className="mt-4 text-gray-600 text-sm">Finding your best matches...</p>
              </div>
            ) : !hasActiveResume ? (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-5xl mb-4">📄</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Resume</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Upload and finalize a resume to get personalized job recommendations based on your skills and experience.
                </p>
                <button
                  onClick={() => navigate(PATHS.resumeReview || '/alumni/resume-review')}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                >
                  Go to Resume Review
                </button>
              </div>
            ) : recommendedError ? (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{recommendedError}</p>
              </div>
            ) : recommendedJobs.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-5xl mb-4">🔍</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Matches Yet</h3>
                <p className="text-gray-600 text-sm">
                  We couldn't find strong matches right now. Check back as new jobs are posted, or browse all jobs.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {recommendedJobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-lg shadow p-4 relative">
                    {/* Match Badge */}
                    <div className="absolute top-3 right-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${getMatchBadgeColor(job.match_percentage)}`}>
                        {job.match_percentage}% Match
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 pr-24 mb-1">{job.title}</h3>
                    <p className="text-orange-600 text-sm font-medium mb-2">{job.company?.name || job.company_name}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
                      {job.location && <span>{job.location}</span>}
                      {job.employment_type && <span>· {job.employment_type.charAt(0).toUpperCase() + job.employment_type.slice(1).replace('-', ' ')}</span>}
                      {formatSalary(job.compensation_min, job.compensation_max) && (
                        <span>· {formatSalary(job.compensation_min, job.compensation_max)}</span>
                      )}
                    </div>
                    {job.description && (
                      <p className="text-gray-600 text-xs mb-3 line-clamp-2">
                        {job.description.length > 150 ? job.description.substring(0, 150) + '...' : job.description}
                      </p>
                    )}
                    <button
                      onClick={() => handleApplyToJob(job)}
                      className="w-full px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                    >
                      Apply Now
                    </button>
                  </div>
                ))}

                {/* Recommended Pagination */}
                {recommendedTotalPages > 1 && (
                  <div className="flex justify-center items-center space-x-4 py-6">
                    <button
                      onClick={() => setRecommendedPage(p => Math.max(1, p - 1))}
                      disabled={recommendedPage === 1}
                      className={`px-3 py-2 rounded-lg text-sm font-medium ${
                        recommendedPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-orange-600 hover:bg-orange-50'
                      }`}
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {recommendedPage} of {recommendedTotalPages}</span>
                    <button
                      onClick={() => setRecommendedPage(p => Math.min(recommendedTotalPages, p + 1))}
                      disabled={recommendedPage === recommendedTotalPages}
                      className={`px-3 py-2 rounded-lg text-sm font-medium ${
                        recommendedPage === recommendedTotalPages ? 'text-gray-400 cursor-not-allowed' : 'text-orange-600 hover:bg-orange-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Desktop Browse Tab */}
        {activeTab === 'browse' && (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Filters Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Filter Opportunities</h2>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Job title, company, keywords..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    placeholder="City, state, or remote"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
                  />
                </div>

                {/* Employment Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Type
                  </label>
                  <select
                    value={filters.employment_type}
                    onChange={(e) => handleFilterChange('employment_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
                  >
                    <option value="">All Types</option>
                    {employmentTypes.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry
                  </label>
                  <select
                    value={filters.industry}
                    onChange={(e) => handleFilterChange('industry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm"
                  >
                    <option value="">All Industries</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                {/* Filter Actions */}
                <div className="flex space-x-2 pt-4">
                  <button
                    onClick={applyFilters}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                  >
                    Apply Filters
                  </button>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Job Listings */}
          <div className="flex-1">
            {jobPostings.length === 0 && !loading ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No opportunities found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your filters or check back later for new postings.
                </p>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {/* Job Cards */}
                <div className="space-y-4">
                  {jobPostings.map((job) => (
                    <div 
                      key={job.id} 
                      className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                      onMouseEnter={() => handleJobView(job)}
                    >
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {job.title}
                            </h3>
                            <p className="text-orange-600 font-medium mb-2">
                              {job.company.name}
                            </p>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              {job.location && (
                                <span className="flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  {job.location}
                                </span>
                              )}
                              {job.employment_type && (
                                <span className="flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 002 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h8z" />
                                  </svg>
                                  {job.employment_type.charAt(0).toUpperCase() + job.employment_type.slice(1).replace('-', ' ')}
                                </span>
                              )}
                              {formatSalary(job.compensation_min, job.compensation_max) && (
                                <span className="flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                  </svg>
                                  {formatSalary(job.compensation_min, job.compensation_max)}
                                </span>
                              )}
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {formatDate(job.posted_date)}
                              </span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            <button
                              onClick={() => handleApplyToJob(job)}
                              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                            >
                              Apply Now
                            </button>
                          </div>
                        </div>

                        {job.description && (
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                            {job.description.length > 300 
                              ? job.description.substring(0, 300) + '...'
                              : job.description
                            }
                          </p>
                        )}

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {job.experience_level && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {job.experience_level}
                            </span>
                          )}
                          {job.remote_work_options && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Remote Available
                            </span>
                          )}
                          {job.company.industry && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {job.company.industry}
                            </span>
                          )}
                          {job.application_deadline && new Date(job.application_deadline) > new Date() && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Deadline: {formatDate(job.application_deadline)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                          currentPage === 1
                            ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {[...Array(Math.min(totalPages, 7))].map((_, index) => {
                        let pageNum;
                        if (totalPages <= 7) {
                          pageNum = index + 1;
                        } else if (currentPage <= 4) {
                          pageNum = index + 1;
                        } else if (currentPage >= totalPages - 3) {
                          pageNum = totalPages - 6 + index;
                        } else {
                          pageNum = currentPage - 3 + index;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                          currentPage === totalPages
                            ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        )}

        {/* Desktop Recommended Tab */}
        {activeTab === 'recommended' && (
          <div className="max-w-4xl mx-auto">
            {recommendedLoading ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Finding your best matches...</p>
              </div>
            ) : !hasActiveResume ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">📄</div>
                <h3 className="text-xl font-medium text-gray-900 mb-3">No Active Resume</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Upload and finalize a resume to get personalized job recommendations based on your skills and experience.
                </p>
                <button
                  onClick={() => navigate(PATHS.resumeReview || '/alumni/resume-review')}
                  className="px-6 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors font-medium"
                >
                  Go to Resume Review
                </button>
              </div>
            ) : recommendedError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-600">{recommendedError}</p>
              </div>
            ) : recommendedJobs.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-medium text-gray-900 mb-3">No Matches Yet</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  We couldn't find strong matches right now. Check back as new jobs are posted, or browse all jobs.
                </p>
                <button
                  onClick={() => setActiveTab('browse')}
                  className="mt-6 px-6 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors font-medium"
                >
                  Browse All Jobs
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {recommendedJobs.map((job) => (
                    <div
                      key={job.id}
                      className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                      onMouseEnter={() => handleJobView(job)}
                    >
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {job.title}
                              </h3>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getMatchBadgeColor(job.match_percentage)}`}>
                                {job.match_percentage}% Match
                              </span>
                            </div>
                            <p className="text-orange-600 font-medium mb-2">
                              {job.company?.name || job.company_name}
                            </p>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              {job.location && (
                                <span className="flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  {job.location}
                                </span>
                              )}
                              {job.employment_type && (
                                <span className="flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4-2h4" />
                                  </svg>
                                  {job.employment_type.charAt(0).toUpperCase() + job.employment_type.slice(1).replace('-', ' ')}
                                </span>
                              )}
                              {formatSalary(job.compensation_min, job.compensation_max) && (
                                <span className="flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                  </svg>
                                  {formatSalary(job.compensation_min, job.compensation_max)}
                                </span>
                              )}
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {formatDate(job.posted_date)}
                              </span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            <button
                              onClick={() => handleApplyToJob(job)}
                              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                            >
                              Apply Now
                            </button>
                          </div>
                        </div>

                        {job.description && (
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                            {job.description.length > 300
                              ? job.description.substring(0, 300) + '...'
                              : job.description
                            }
                          </p>
                        )}

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {job.experience_level && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {job.experience_level}
                            </span>
                          )}
                          {job.remote_work_options && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Remote Available
                            </span>
                          )}
                          {(job.company?.industry || job.industry) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {job.company?.industry || job.industry}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recommended Pagination */}
                {recommendedTotalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setRecommendedPage(p => Math.max(1, p - 1))}
                        disabled={recommendedPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 rounded-l-md border text-sm font-medium ${
                          recommendedPage === 1
                            ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        Previous
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        Page {recommendedPage} of {recommendedTotalPages}
                      </span>
                      <button
                        onClick={() => setRecommendedPage(p => Math.min(recommendedTotalPages, p + 1))}
                        disabled={recommendedPage === recommendedTotalPages}
                        className={`relative inline-flex items-center px-4 py-2 rounded-r-md border text-sm font-medium ${
                          recommendedPage === recommendedTotalPages
                            ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileAlumniBottomNav 
        activeSection="jobs"
        onNavigate={(path) => navigate(path)}
      />
    </div>
  );
};

export default AlumniJobBoard;